import { useState, useEffect, useRef } from "react";
import { Car, Home as HomeIcon, HeartPulse, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { C } from "./data";
import { Serif, Label } from "./ui";
import { getEnrichment, saveEnrichment } from "./consumerAuth";

const TABS = [
  { id: "vehicle", label: "Vehicle", icon: Car },
  { id: "home", label: "Home", icon: HomeIcon },
  { id: "medical", label: "Medical", icon: HeartPulse },
];

const FIELDS = {
  vehicle: [["Make", "make"], ["Model", "model"], ["Year", "year"], ["Colour", "colour"], ["Registration", "reg"], ["Tracker", "tracker"], ["Notes", "notes"]],
  home: [["Address", "address"], ["Armed response", "provider"], ["Alarm system", "alarm"], ["Key holder", "keyholder"], ["Panic points", "panicPoints"]],
  medical: [["Blood type", "bloodType"], ["Allergies", "allergies"], ["Conditions", "conditions"], ["Medical aid", "medicalAid"], ["Membership no.", "medicalAidNo"], ["Next of kin", "nextOfKin"]],
};

export default function Profile({ tab: initial = "vehicle", user, cover, onSignOut, onReplayTour, nav }) {
  const [tab, setTab] = useState(initial);
  const [data, setData] = useState({ vehicle: {}, home: {}, medical: {} });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const editingRef = useRef(false);
  const beginEdit = () => { editingRef.current = true; setEditing(true); };
  const endEdit = () => { editingRef.current = false; setEditing(false); };

  useEffect(() => {
    getEnrichment()
      .then((e) => { if (!editingRef.current) { setData({ vehicle: e.vehicle || {}, home: e.home || {}, medical: e.medical || {} }); setPrefilled(!!e.prefilled); } })
      .catch((e) => console.error("enrichment load failed", e))
      .finally(() => setLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fullName = (user && user.name) || "Your profile";
  const insurer = cover ? `ResQ1 (${cover.insurer})` : "ResQ1 Emergency Protection";
  const setField = (key, val) => setData((d) => ({ ...d, [tab]: { ...d[tab], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await saveEnrichment(data);
      toast.success("Details saved — responders will see these");
      setPrefilled(false);
      endEdit();
    } catch (e) {
      toast.error("Couldn't save details", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: "24px 22px 30px" }} data-testid="consumer-profile">
      <Label>Profile enrichment</Label>
      <Serif size={26} style={{ margin: "5px 0 4px" }}>{fullName}</Serif>
      <div style={{ fontSize: 12, color: C.slate, marginBottom: 20 }}>{insurer}{cover?.policy_number ? ` · Policy ${cover.policy_number}` : ""}</div>

      {prefilled && !editing && (
        <div data-testid="profile-prefill-banner" style={{ background: `${C.amber}1a`, border: `1px solid ${C.amber}55`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>We pulled these from your ResQ1 records</div>
          <div style={{ fontSize: 12, color: C.slate, marginTop: 3, lineHeight: 1.5 }}>Please review each tab and tap <b>Confirm</b> to save — responders use these on arrival.</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {TABS.map((t) => {
          const on = tab === t.id; const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => { setTab(t.id); endEdit(); }} data-testid={`profile-tab-${t.id}`}
              style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, padding: "12px 6px", background: on ? C.navy : "#fff", color: on ? "#fff" : C.slate, border: `1px solid ${on ? C.navy : "rgba(26,30,36,0.1)"}`, borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              <Icon size={18} /> {t.label}
            </button>
          );
        })}
      </div>

      <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 12, overflow: "hidden" }}>
        {FIELDS[tab].map(([label, key], i) => (
          <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, padding: "11px 15px", borderTop: i ? "1px solid rgba(26,30,36,0.06)" : "none" }}>
            <div style={{ fontSize: 12, color: C.slate, flexShrink: 0 }}>{label}</div>
            {editing ? (
              <input value={data[tab][key] || ""} onChange={(e) => setField(key, e.target.value)} data-testid={`profile-input-${key}`}
                style={{ flex: 1, maxWidth: 190, padding: "7px 10px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 7, fontSize: 13, outline: "none", textAlign: "right", color: C.ink }} />
            ) : (
              <div style={{ fontSize: 13, color: data[tab][key] ? C.ink : C.slate, fontWeight: 500, textAlign: "right" }} data-testid={`profile-value-${key}`}>{data[tab][key] || "—"}</div>
            )}
          </div>
        ))}
      </div>

      {editing ? (
        <button onClick={save} disabled={saving} data-testid="profile-save"
          style={{ marginTop: 16, width: "100%", padding: 13, background: C.red, color: "#fff", border: 0, borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <Check size={16} /> {saving ? "Saving…" : `Save ${TABS.find((t) => t.id === tab).label.toLowerCase()} details`}
        </button>
      ) : (
        <button onClick={beginEdit} disabled={!loaded} data-testid="profile-edit"
          style={{ marginTop: 16, width: "100%", padding: 13, background: prefilled ? C.red : "transparent", color: prefilled ? "#fff" : C.navy, border: prefilled ? 0 : `1.5px solid ${C.navy}`, borderRadius: 12, fontSize: 14, fontWeight: prefilled ? 700 : 600, cursor: loaded ? "pointer" : "default", opacity: loaded ? 1 : 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {prefilled ? <Check size={16} /> : <Pencil size={15} />} {loaded ? (prefilled ? `Review & confirm ${TABS.find((t) => t.id === tab).label.toLowerCase()} details` : `Edit ${TABS.find((t) => t.id === tab).label.toLowerCase()} details`) : "Loading…"}
        </button>
      )}
      <div style={{ marginTop: 14, fontSize: 11, color: C.slate, textAlign: "center", lineHeight: 1.5 }}>
        These details reach responders the moment they arrive — the more we know, the faster we help.
      </div>
      {nav && (
        <button onClick={() => nav("dependents")} data-testid="profile-household-link"
          style={{ marginTop: 14, width: "100%", padding: 12, background: "transparent", color: C.navy, border: `1px solid ${C.navy}33`, borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
          My household — see and fix who is on my cover
        </button>
      )}
      {onReplayTour && (
        <button onClick={onReplayTour} data-testid="profile-replay-tour"
          style={{ marginTop: 14, width: "100%", padding: 11, background: "transparent", color: C.navy, border: `1px solid ${C.navy}33`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Replay app tour
        </button>
      )}
      {onSignOut && (
        <button onClick={onSignOut} data-testid="consumer-signout"
          style={{ marginTop: 14, width: "100%", padding: 11, background: "transparent", color: C.slate, border: "1px solid rgba(26,30,36,0.15)", borderRadius: 10, fontSize: 13, cursor: "pointer" }}>
          Sign out
        </button>
      )}
    </div>
  );
}
